import {
  IBanList,
  IGameRecord,
  IMatch,
  IMatchGamePlayer,
  IPlayer,
  IPlayerGameStats,
  ITeamInfo,
} from "../types/types";

const ROLE_ORDER: Record<string, number> = {
  TOP: 0,
  JUNGLE: 1,
  MIDDLE: 2,
  MID: 2,
  BOTTOM: 3,
  ADC: 3,
  UTILITY: 4,
  SUPPORT: 4,
  NONE: 5,
};

export interface TeamSideMapping {
  blueTeamId: string;
  redTeamId: string;
}

const countTeamSides = (
  game: IGameRecord,
  teamOne: string,
  teamTwo: string,
  leagueTeamForPlayer: (player: IMatchGamePlayer) => string | null,
): TeamSideMapping | null => {
  let blueOne = 0;
  let blueTwo = 0;
  let redOne = 0;
  let redTwo = 0;

  for (const player of game.players ?? []) {
    const leagueTeam = leagueTeamForPlayer(player);
    if (!leagueTeam || (leagueTeam !== teamOne && leagueTeam !== teamTwo)) {
      continue;
    }

    if (player.team_id === 100) {
      if (leagueTeam === teamOne) blueOne++;
      else blueTwo++;
    } else if (player.team_id === 200) {
      if (leagueTeam === teamOne) redOne++;
      else redTwo++;
    }
  }

  if (blueOne > blueTwo && blueOne > 0) {
    return { blueTeamId: teamOne, redTeamId: teamTwo };
  }
  if (blueTwo > blueOne && blueTwo > 0) {
    return { blueTeamId: teamTwo, redTeamId: teamOne };
  }
  if (redOne > redTwo && redOne > 0) {
    return { blueTeamId: teamTwo, redTeamId: teamOne };
  }
  if (redTwo > redOne && redTwo > 0) {
    return { blueTeamId: teamOne, redTeamId: teamTwo };
  }

  return null;
};

export const resolveTeamSides = (
  game: IGameRecord,
  teamOne: string,
  teamTwo: string,
  rosterByPuuid: Map<string, IPlayer>,
  gameStats: IPlayerGameStats[] = [],
): TeamSideMapping => {
  const fromRoster = countTeamSides(game, teamOne, teamTwo, (player) => {
    return rosterByPuuid.get(player.puuid)?.teamId ?? null;
  });
  if (fromRoster) return fromRoster;

  const statsByPuuid = new Map<string, IPlayerGameStats>();
  for (const stat of gameStats) {
    if (stat.riotPuuid) {
      statsByPuuid.set(stat.riotPuuid, stat);
    }
  }

  const fromStats = countTeamSides(game, teamOne, teamTwo, (player) => {
    return statsByPuuid.get(player.puuid)?.teamId ?? null;
  });
  if (fromStats) return fromStats;

  return { blueTeamId: teamOne, redTeamId: teamTwo };
};

export const sortPlayersByRole = (players: IMatchGamePlayer[]): IMatchGamePlayer[] =>
  [...players].sort(
    (a, b) => (ROLE_ORDER[normalizeRole(a.role)] ?? 9) - (ROLE_ORDER[normalizeRole(b.role)] ?? 9),
  );

export const normalizeRole = (role: string): string => {
  const value = role.trim().toUpperCase();
  if (value === "MIDDLE") return "MID";
  if (value === "BOTTOM") return "ADC";
  if (value === "UTILITY") return "SUPPORT";
  return value;
};

export const formatGameDuration = (minutes: number): string => {
  const totalSeconds = Math.round(minutes * 60);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins)}:${secs.toString().padStart(2, "0")}`;
};

export const formatCompactNumber = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return String(value);
};

export const formatSignedNumber = (value: number): string => {
  if (value > 0) return `+${String(value)}`;
  return String(value);
};

export const formatPct = (value: number | null): string =>
  value == null ? "—" : `${value.toFixed(1)}%`;

export const buildRosterByPuuid = (players: IPlayer[]): Map<string, IPlayer> => {
  const map = new Map<string, IPlayer>();
  for (const player of players) {
    const puuid = player.riot_puuid.trim();
    if (!puuid || puuid === "PENDING_ROSTER_IMPORT" || puuid === "MISSING REMOVE THIS ENTRY") {
      continue;
    }
    map.set(puuid, player);
  }
  return map;
};

/** Prefer parent team id from ITeamInfo so side resolution matches roster membership. */
export const buildRosterByPuuidFromTeamInfos = (
  teams: ITeamInfo[] | undefined,
): Map<string, IPlayer> => {
  const map = new Map<string, IPlayer>();
  for (const team of teams ?? []) {
    for (const player of team.players ?? []) {
      const puuid = player.riot_puuid.trim();
      if (!puuid || puuid === "PENDING_ROSTER_IMPORT" || puuid === "MISSING REMOVE THIS ENTRY") {
        continue;
      }
      map.set(puuid, { ...player, teamId: team.id });
    }
  }
  return map;
};

export const riotSideForLeagueTeam = (
  leagueTeamId: string,
  mapping: TeamSideMapping,
): "blue" | "red" => (leagueTeamId === mapping.blueTeamId ? "blue" : "red");

export const indexPlayerStats = (stats: IPlayerGameStats[]) => {
  const byPuuid = new Map<string, IPlayerGameStats>();
  for (const stat of stats) {
    if (stat.riotPuuid) {
      byPuuid.set(stat.riotPuuid, stat);
    }
  }
  return { byPuuid };
};

export const resolvePlayerName = (
  player: IMatchGamePlayer,
  rosterByPuuid: Map<string, IPlayer>,
  statsByPuuid: Map<string, IPlayerGameStats>,
): string => {
  const rosterPlayer = rosterByPuuid.get(player.puuid);
  if (rosterPlayer) {
    if (rosterPlayer.displayName.trim()) return rosterPlayer.displayName;
    if (rosterPlayer.name.trim()) return rosterPlayer.name;
  }

  const stat = statsByPuuid.get(player.puuid);
  if (stat && stat.playerName.trim()) return stat.playerName;

  return "Unknown player";
};

export const teamWonGame = (
  leagueTeamId: string,
  mapping: TeamSideMapping,
  game: IGameRecord,
): boolean => {
  if (leagueTeamId === mapping.blueTeamId) return game.blue_win;
  return game.red_win;
};

export const teamBans = (
  leagueTeamId: string,
  mapping: TeamSideMapping,
  game: IGameRecord,
): IBanList[] => {
  const isBlue = leagueTeamId === mapping.blueTeamId;
  return isBlue ? (game.blue_bans ?? []) : (game.red_bans ?? []);
};

export const teamObjectives = (
  leagueTeamId: string,
  mapping: TeamSideMapping,
  game: IGameRecord,
) => {
  const isBlue = leagueTeamId === mapping.blueTeamId;
  return {
    turrets: isBlue ? game.blue_turrets : game.red_turrets,
    dragons: isBlue ? game.blue_dragons : game.red_dragons,
    dragonTypes: isBlue ? (game.blue_dragon_types ?? []) : (game.red_dragon_types ?? []),
    barons: isBlue ? game.blue_barons : game.red_barons,
    grubs: isBlue ? game.blue_grubs : game.red_grubs,
    herald: isBlue ? game.blue_herald : game.red_herald,
    elders: isBlue ? game.blue_elders : game.red_elders,
    bans: teamBans(leagueTeamId, mapping, game),
  };
};

export const teamKillTotal = (players: IMatchGamePlayer[]): number =>
  players.reduce((sum, player) => sum + player.champions_killed, 0);

export const computeKpPct = (
  player: IMatchGamePlayer,
  teamPlayers: IMatchGamePlayer[],
): number | null => {
  const teamKills = teamKillTotal(teamPlayers);
  if (teamKills <= 0) return null;
  return ((player.champions_killed + player.assists) / teamKills) * 100;
};

const findLaneOpponentByParticipantOrder = (
  player: IMatchGamePlayer,
  allPlayers: IMatchGamePlayer[],
): IMatchGamePlayer | undefined => {
  const playerIndex = allPlayers.indexOf(player);
  if (playerIndex < 0) return undefined;

  const opponentIndex = playerIndex < 5 ? playerIndex + 5 : playerIndex - 5;
  if (opponentIndex < 0 || opponentIndex >= allPlayers.length) return undefined;

  const opponent = allPlayers[opponentIndex];
  if (opponent.team_id === player.team_id) return undefined;
  return opponent;
};

const findLaneOpponentByRoleIndex = (
  player: IMatchGamePlayer,
  allPlayers: IMatchGamePlayer[],
): IMatchGamePlayer | undefined => {
  const teamPlayers = sortPlayersByRole(
    allPlayers.filter((candidate) => candidate.team_id === player.team_id),
  );
  const enemyPlayers = sortPlayersByRole(
    allPlayers.filter((candidate) => candidate.team_id !== player.team_id),
  );

  const indexByPuuid = teamPlayers.findIndex((candidate) => candidate.puuid === player.puuid);
  if (indexByPuuid >= 0 && indexByPuuid < enemyPlayers.length) {
    return enemyPlayers[indexByPuuid];
  }

  const indexByReference = teamPlayers.indexOf(player);
  if (indexByReference >= 0 && indexByReference < enemyPlayers.length) {
    return enemyPlayers[indexByReference];
  }

  return undefined;
};

export const findLaneOpponent = (
  player: IMatchGamePlayer,
  allPlayers: IMatchGamePlayer[],
): IMatchGamePlayer | undefined => {
  const role = normalizeRole(player.role);
  if (role && role !== "NONE") {
    const byRole = allPlayers.find(
      (candidate) => candidate.team_id !== player.team_id && normalizeRole(candidate.role) === role,
    );
    if (byRole) return byRole;
  }

  const byParticipantOrder = findLaneOpponentByParticipantOrder(player, allPlayers);
  if (byParticipantOrder) return byParticipantOrder;

  return findLaneOpponentByRoleIndex(player, allPlayers);
};

export const riotParticipantOrder = (players: IMatchGamePlayer[]): IMatchGamePlayer[] => {
  const blue = players.filter((player) => player.team_id === 100);
  const red = players.filter((player) => player.team_id === 200);
  if (blue.length === 0 || red.length === 0) return players;
  return [...blue, ...red];
};

export const hasCs15Data = (players: IMatchGamePlayer[]): boolean =>
  players.some((player) => player.cs_at_15 > 0);

export const computeCs15Diff = (
  player: IMatchGamePlayer,
  allPlayers: IMatchGamePlayer[],
): number | null => {
  if (!hasCs15Data(allPlayers)) return null;
  const orderedPlayers = riotParticipantOrder(allPlayers);
  const opponent = findLaneOpponent(player, orderedPlayers);
  if (!opponent) return null;
  return player.cs_at_15 - opponent.cs_at_15;
};

export const matchTeamsFromDetail = (match: IMatch): { teamOne: string; teamTwo: string } => ({
  teamOne: match.teamOne,
  teamTwo: match.teamTwo,
});
